import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';

import { getCatalogPublishImpact, getCatalogRuns, getCatalogWorkspace, publishCatalogRun, pullCatalogBrandSnapshot } from '../../api/catalogs';
import CurrentCatalog from './CurrentCatalog';
import CatalogSync from './CatalogSync';
import CatalogIntegrationSelector from './CatalogIntegrationSelector';
import CatalogPublishImpactModal from './CatalogPublishImpactModal';
import CatalogRunHistory from './CatalogRunHistory';
import './CatalogWorkspace.scss';

// Matches the backend's stale-publication takeover window.
const PUBLICATION_STALE_MS = 60 * 60 * 1000;

const WORKSPACE_SECTIONS = [
  ['catalog', 'Catalog', ''],
  ['versions', 'Versions', '/versions']
];

const CatalogWorkspace = ({ brandKey, BrandIntegration_id: routeBrandIntegrationId = null, runNumber = null, section = 'catalog' }) => {
  const history = useHistory();
  const [workspace, setWorkspace] = useState(null);
  const [runs, setRuns] = useState(null);
  const [viewedRun, setViewedRun] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPullingSnapshot, setIsPullingSnapshot] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoadingPublishImpact, setIsLoadingPublishImpact] = useState(false);
  const [publishImpact, setPublishImpact] = useState(null);
  const [artifactRevision, setArtifactRevision] = useState(0);
  const selectedIntegrationIdRef = useRef(null);
  const previousRunStatusRef = useRef(null);
  const publishQueuedAtRef = useRef(null);
  const hasLoadedRouteRef = useRef(false);
  const loadedBrandKeyRef = useRef(null);

  const load = useCallback(BrandIntegration_id => {
    const requestedBrandKey = brandKey;
    const isSelectedIntegration = () =>
      loadedBrandKeyRef.current === requestedBrandKey &&
      (BrandIntegration_id == null || selectedIntegrationIdRef.current == null || BrandIntegration_id === selectedIntegrationIdRef.current);
    if (isSelectedIntegration()) setError(null);
    return getCatalogWorkspace(brandKey, BrandIntegration_id)
      .then(nextWorkspace => {
        if (!isSelectedIntegration()) return;
        selectedIntegrationIdRef.current = nextWorkspace.BrandIntegration_id;
        setWorkspace(nextWorkspace);
      })
      .catch(loadError => {
        if (isSelectedIntegration()) setError(loadError);
      })
      .finally(() => {
        if (isSelectedIntegration()) setIsLoading(false);
      });
  }, [brandKey]);

  useEffect(() => {
    const isSameLoadedRoute =
      hasLoadedRouteRef.current && loadedBrandKeyRef.current === brandKey && routeBrandIntegrationId === selectedIntegrationIdRef.current;
    if (isSameLoadedRoute) return;
    hasLoadedRouteRef.current = true;
    loadedBrandKeyRef.current = brandKey;
    selectedIntegrationIdRef.current = routeBrandIntegrationId;
    setIsLoading(true);
    setPublishImpact(null);
    load(routeBrandIntegrationId);
  }, [brandKey, load, routeBrandIntegrationId]);

  const run = workspace?.run || null;
  const routeRun = viewedRun && viewedRun.number === runNumber ? viewedRun : null;
  const displayRun = runNumber ? routeRun : run;
  const runStatus = run?.status || null;
  const BrandIntegration_id = workspace?.BrandIntegration_id || null;
  const hasRunsInFlight = (workspace?.inFlightRunCount || 0) > 0;
  const isPublicationInFlight = runStatus === 'publishing' && new Date() - new Date(run?.publication?.updatedAt || 0) <= PUBLICATION_STALE_MS;

  useEffect(() => {
    if (!BrandIntegration_id) return;
    const canonicalPath = `/catalogs/${brandKey}/integration-${BrandIntegration_id}${runNumber ? `/run-${runNumber}` : section !== 'catalog' ? `/${section}` : ''}`;
    if (history.location.pathname !== canonicalPath) history.replace(canonicalPath);
  }, [BrandIntegration_id, brandKey, history, runNumber, section]);

  useEffect(() => {
    if (!hasRunsInFlight && !isPublicationInFlight) return;
    const poll = setInterval(() => load(BrandIntegration_id), 3000);
    return () => clearInterval(poll);
  }, [BrandIntegration_id, hasRunsInFlight, isPublicationInFlight, load]);

  const runsStamp = [
    runStatus,
    run?._id,
    workspace?.state?.lastPublication?.completedAt,
    ...(workspace?.activeRuns || []).map(activeRun => `${activeRun._id}:${activeRun.status}:${activeRun.currentStep}:${activeRun.updatedAt}`)
  ].join('|');
  useEffect(() => {
    if (!BrandIntegration_id) return;
    let isStale = false;
    getCatalogRuns(brandKey, BrandIntegration_id)
      .then(runHistory => {
        if (!isStale) setRuns(runHistory.runs);
      })
      .catch(() => {
        if (!isStale) setRuns(current => current || []);
      });
    return () => {
      isStale = true;
    };
  }, [brandKey, BrandIntegration_id, runsStamp]);

  useEffect(() => {
    if (!runNumber || !BrandIntegration_id) {
      setViewedRun(null);
      return undefined;
    }
    const fromList = (runs || []).find(candidate => candidate.number === runNumber);
    if (fromList) {
      setViewedRun(fromList);
      return undefined;
    }
    if (runs === null) return undefined;
    let isStale = false;
    getCatalogRuns(brandKey, BrandIntegration_id, runNumber)
      .then(runHistory => {
        if (!isStale) setViewedRun(runHistory.runs[0] || false);
      })
      .catch(() => {
        if (!isStale) setViewedRun(false);
      });
    return () => {
      isStale = true;
    };
  }, [brandKey, BrandIntegration_id, runNumber, runs]);

  useEffect(() => {
    if (!workspace) return;
    const status = workspace.run?.status;
    const previousStatus = previousRunStatusRef.current;
    previousRunStatusRef.current = status;
    if (status !== 'ready' || (previousStatus !== 'queued' && previousStatus !== 'running')) return;
    const stats = workspace.run?.stats || {};
    cogoToast.success(`Version ready: ${Number(stats.materializedVariants || 0).toLocaleString()} variants from ${Number(stats.captureRows || 0).toLocaleString()} rows.`);
  }, [workspace]);

  useEffect(() => {
    if (!workspace || !publishQueuedAtRef.current) return;
    if (workspace.run?.status === 'publishing') return;
    const queuedAt = publishQueuedAtRef.current;
    publishQueuedAtRef.current = null;
    const lastPublication = workspace.state?.lastPublication;
    if (workspace.run?.status === 'needs_repair' || lastPublication?.status !== 'complete' || new Date(lastPublication.completedAt) <= queuedAt) return;
    const applied = lastPublication.applied || {};
    cogoToast.success(
      `Published: ${Number(applied.variantsMinted || 0).toLocaleString()} variants minted, ${Number(applied.siblingsMinted || 0).toLocaleString()} siblings minted, ` +
        `${Number(applied.variantsUpdated || 0) + Number(applied.siblingsUpdated || 0)} rows refreshed, ${Number(applied.siblingsRetired || 0)} retired.`
    );
  }, [workspace]);

  const pullSnapshot = async () => {
    setIsPullingSnapshot(true);
    try {
      await pullCatalogBrandSnapshot(brandKey, BrandIntegration_id);
    } catch (snapshotError) {
      cogoToast.error(snapshotError);
    } finally {
      await load(BrandIntegration_id);
      setIsPullingSnapshot(false);
    }
  };

  const publishRun = async (overrides = []) => {
    // The reviewed impact pins the exact version it was computed for; without it
    // (the repair path) pin the version on screen so a run that turns ready
    // mid-review can never ship under another version's summary.
    const Run_id = publishImpact?.version?.Run_id || (displayRun?._id ? String(displayRun._id) : null);
    setIsPublishing(true);
    try {
      await publishCatalogRun(brandKey, BrandIntegration_id, Run_id, overrides);
      publishQueuedAtRef.current = Date.now();
      setPublishImpact(null);
    } catch (publishError) {
      cogoToast.error(publishError, { hideAfter: 10 });
    } finally {
      await load(BrandIntegration_id);
      setIsPublishing(false);
    }
  };

  const reviewPublish = async () => {
    if (publicationNeedsRepair) return publishRun();
    const integrationId = BrandIntegration_id;
    setIsLoadingPublishImpact(true);
    try {
      const impact = await getCatalogPublishImpact(brandKey, integrationId, displayRun?._id ? String(displayRun._id) : null);
      if (selectedIntegrationIdRef.current === integrationId) setPublishImpact(impact);
    } catch (impactError) {
      cogoToast.error(impactError, { hideAfter: 10 });
    } finally {
      setIsLoadingPublishImpact(false);
    }
  };

  const selectIntegration = integrationId => {
    if (integrationId === BrandIntegration_id) return;
    selectedIntegrationIdRef.current = integrationId;
    setIsLoading(true);
    setPublishImpact(null);
    setRuns(null);
    previousRunStatusRef.current = null;
    publishQueuedAtRef.current = null;
    history.push(`/catalogs/${brandKey}/integration-${integrationId}`);
    load(integrationId);
  };

  if (isLoading) return <div className='catalogs-page'><div className='catalogs-empty'><LoadingLabel>Loading catalog workspace…</LoadingLabel></div></div>;
  if (error) return <div className='catalogs-page'><div className='catalogs-error'>{error}</div></div>;
  if (!workspace) return null;

  if (!workspace.integrations?.length) {
    return (
      <div className='catalogs-page'>
        <div className='catalogs-workspace-header'>
          <div className='catalogs-workspace-title-block'>
            <Link className='catalogs-back-link' to='/catalogs'>← All brands</Link>
            <div className='catalogs-workspace-title'>
              <h1>{workspace.brand.name || workspace.brand.brandKey}</h1>
            </div>
            {workspace.brand.domain && <div className='catalogs-muted'>{workspace.brand.domain}</div>}
          </div>
        </div>
        <div className='catalogs-empty compact'>This brand has no product-syncing Shopify integrations.</div>
      </div>
    );
  }

  const { brand, state } = workspace;
  const displayRunStatus = displayRun?.status || null;
  const failedRun = displayRunStatus === 'failed' ? displayRun : !runNumber && workspace.latestActivity?.status === 'failed' ? workspace.latestActivity : null;
  const isCurrentLive = displayRun?.role === 'live';
  const isRunPublishable =
    ['ready', 'publishing', 'needs_repair'].includes(displayRunStatus) || (displayRunStatus === 'published' && !isCurrentLive);
  const isRunViewable = Boolean(displayRun?.number && displayRun?.tree?.s3Key && !isCurrentLive);
  const isPublicationStale = displayRunStatus === 'publishing' && new Date() - new Date(displayRun?.publication?.updatedAt || 0) > PUBLICATION_STALE_MS;
  const isPublicationRunning = displayRunStatus === 'publishing' && !isPublicationStale;
  const publicationNeedsRepair = displayRunStatus === 'needs_repair' || isPublicationStale;
  const displayRunNumber = displayRun?.number || null;
  const isRestoring = displayRunStatus === 'published';
  const publishLabel = getPublishLabel({ isLoadingPublishImpact, isPublishing, isPublicationRunning, publicationNeedsRepair, isRestoring, runNumber: displayRunNumber });
  const isPublishLoading = isLoadingPublishImpact || isPublishing || isPublicationRunning;
  const isPublishDisabled = !isRunPublishable || isPublishLoading;
  const currentLabel = state?.live?.number ? `Current (#${state.live.number})` : 'Current';
  const versionLabel = displayRun?.role === 'previously_live' ? `Previously live (#${displayRunNumber})` : `Draft (#${displayRunNumber})`;
  const artifactStamp = [artifactRevision, run?._id, runStatus, state?.latestSnapshot?.fingerprint, state?.lastPublication?.completedAt].join('|');
  const artifactsChanged = () => setArtifactRevision(current => current + 1);

  return (
    <div className='catalogs-page'>
      <div className='catalogs-workspace-header'>
        <div className='catalogs-workspace-title-block'>
          <Link className='catalogs-back-link' to='/catalogs'>← All brands</Link>
          <div className='catalogs-workspace-title'>
            <h1>{brand.name || brand.brandKey}</h1>
            {Boolean(brand.shopmyBrandId) && <span className='catalogs-brand-id'>Brand ID {brand.shopmyBrandId}</span>}
          </div>
        </div>
        <CatalogIntegrationSelector integrations={workspace.integrations} selectedId={BrandIntegration_id} onSelect={selectIntegration} />
      </div>

      {runNumber && (viewedRun === false || (routeRun && run?.number !== runNumber)) && (
        <div className='catalogs-viewing-run'>
          <span>
            {viewedRun === false
              ? `Version #${runNumber} was not found for this integration.`
              : `Viewing ${routeRun?.role === 'previously_live' ? 'previously live ' : routeRun?.role === 'live' ? 'current ' : ''}version #${runNumber}`}
          </span>
          <Link to={`/catalogs/${brandKey}/integration-${BrandIntegration_id}`}>Back to latest</Link>
        </div>
      )}

      {failedRun && (
        <div className='catalogs-error'>
          {failedRun.number ? `Version #${failedRun.number} failed: ` : 'The last catalog run failed: '}{(failedRun.errors || []).slice(0, 3).join('; ')}
          {failedRun.stderrTail && <pre className='catalogs-workspace-script-content'>{failedRun.stderrTail}</pre>}
        </div>
      )}

      {publicationNeedsRepair && (
        <div className='catalogs-error'>
          {displayRunNumber ? `Publish of version #${displayRunNumber} stopped after ` : 'Publish stopped after '}{String(displayRun?.publication?.stage || 'starting').replaceAll('_', ' ')}: {displayRun?.publication?.error?.message || 'A required sync step failed.'} Retry the publish to resume safely.
        </div>
      )}

      {isRunPublishable && (
        <div className='catalogs-compare-actionbar'>
          <div>
            <strong>{isRestoring ? `Restore version #${displayRunNumber}` : `Version #${displayRunNumber} ready`}</strong>
            <span>
              {displayRun?.autoPublishHoldMessage
                ? `Held by checks: ${displayRun.autoPublishHoldMessage}`
                : "Publishing writes these variants and variant siblings to MySQL and promotes the version's snapshot, script, and tree to live."}
            </span>
          </div>
          <div className='catalogs-compare-actionbar-actions'>
            {section === 'versions' && isRunViewable && (
              <Link className='catalogs-button ghost compact' to={`/catalogs/${brandKey}/integration-${BrandIntegration_id}/run-${displayRunNumber}`}>
                {displayRun.role === 'previously_live' ? 'Compare version' : 'Review draft'}
              </Link>
            )}
            <button className='catalogs-button compact' disabled={isPublishDisabled} onClick={reviewPublish}>
              {isPublishLoading ? <LoadingLabel>{publishLabel}</LoadingLabel> : publishLabel}
            </button>
          </div>
        </div>
      )}

      <div className='catalogs-workspace-tabs'>
        {WORKSPACE_SECTIONS.map(([key, label, suffix]) => (
          <Link
            className={`catalogs-workspace-tab${section === key ? ' active' : ''}`}
            key={key}
            to={`/catalogs/${brandKey}/integration-${BrandIntegration_id}${suffix}`}
          >
            {label}
          </Link>
        ))}
        <CatalogSync
          brandKey={brandKey}
          BrandIntegration_id={BrandIntegration_id}
          activeRuns={workspace.activeRuns || []}
          artifactRevision={artifactStamp}
          integrations={workspace.integrations}
          isPullingSnapshot={isPullingSnapshot}
          latestSnapshot={state?.latestSnapshot || null}
          liveScript={state?.live?.script || null}
          liveSnapshot={state?.live?.snapshot || null}
          mode='sync'
          runs={runs}
          onArtifactsChanged={artifactsChanged}
          onCreated={() => load(BrandIntegration_id)}
          onPullSnapshot={pullSnapshot}
        />
      </div>
      {section === 'versions' && (
        <div className='catalogs-versions'>
          <CatalogSync
            brandKey={brandKey}
            BrandIntegration_id={BrandIntegration_id}
            activeRuns={workspace.activeRuns || []}
            artifactRevision={artifactStamp}
            integrations={workspace.integrations}
            isPullingSnapshot={isPullingSnapshot}
            latestSnapshot={state?.latestSnapshot || null}
            liveScript={state?.live?.script || null}
            liveSnapshot={state?.live?.snapshot || null}
            mode='compose'
            runs={runs}
            onArtifactsChanged={artifactsChanged}
            onCreated={() => load(BrandIntegration_id)}
            onPullSnapshot={pullSnapshot}
          />
          <div className='catalogs-versions-history'>
            <h2>History</h2>
            <CatalogRunHistory brandKey={brandKey} BrandIntegration_id={BrandIntegration_id} runs={runs} />
          </div>
        </div>
      )}
      {section === 'catalog' && (
        <CurrentCatalog
          brandKey={brandKey}
          BrandIntegration_id={BrandIntegration_id}
          currentLabel={currentLabel}
          draftStamp={displayRun?.completedAt || null}
          isDraftReady={isRunViewable}
          Run_id={isRunViewable ? String(displayRun._id) : null}
          versionLabel={isRunViewable ? versionLabel : 'Draft'}
        />
      )}
      {publishImpact && (
        <CatalogPublishImpactModal
          impact={publishImpact}
          isPublishing={isPublishing}
          onCancel={() => setPublishImpact(null)}
          onConfirm={() => publishRun((publishImpact.warnings || []).filter(warning => warning.isOverrideable).map(warning => warning.key))}
        />
      )}
    </div>
  );
};

const getPublishLabel = ({ isLoadingPublishImpact, isPublishing, isPublicationRunning, publicationNeedsRepair, isRestoring, runNumber }) => {
  const numberSuffix = runNumber ? ` #${runNumber}` : '';
  if (isLoadingPublishImpact) return 'Reviewing';
  if (isPublishing || isPublicationRunning) return `Publishing${numberSuffix}`;
  if (publicationNeedsRepair) return `Retry publish${numberSuffix}`;
  if (isRestoring) return `Restore version${numberSuffix}`;
  return `Publish version${numberSuffix}`;
};

const LoadingLabel = ({ children }) => <><FontAwesomeIcon className='catalogs-loading-icon' icon={faSpinner} spin />{children}</>;

export default CatalogWorkspace;
